/**
 * Motion vocabulary.
 *
 * One shared set of variants and durations, so the whole app moves like one
 * thing. A bespoke easing per component is what makes a UI feel assembled
 * from parts rather than designed.
 *
 * Accessibility: <MotionConfig reducedMotion="user"> in main.tsx makes Motion
 * skip transform/layout animation for anyone who asked for reduced motion,
 * while opacity still fades — the substitution, not deletion, principle
 * (WCAG 2.3.3). Anything decorative and looping is additionally gated on
 * useReducedMotion() below.
 */

import { motion, useReducedMotion, type Variants, type Transition } from 'motion/react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

// ── Timing ─────────────────────────────────────────────────────────────
// Matches --ease-out-soft in index.css. Two definitions of the same curve is
// how they drift apart, but CSS custom properties aren't readable from JS
// without a getComputedStyle round trip — so they're mirrored deliberately.
export const EASE_OUT_SOFT = [0.22, 1, 0.36, 1] as const;
export const EASE_SPRING = [0.34, 1.56, 0.64, 1] as const;

export const transitions = {
  quick: { duration: 0.2, ease: EASE_OUT_SOFT },
  base: { duration: 0.35, ease: EASE_OUT_SOFT },
  slow: { duration: 0.6, ease: EASE_OUT_SOFT },
  spring: { type: 'spring', stiffness: 380, damping: 30 },
  softSpring: { type: 'spring', stiffness: 200, damping: 24 },
} satisfies Record<string, Transition>;

// ── Variants ───────────────────────────────────────────────────────────

export const fadeUp: Variants = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: transitions.base },
  exit: { opacity: 0, y: -8, transition: transitions.quick },
};

export const fadeIn: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: transitions.base },
  exit: { opacity: 0, transition: transitions.quick },
};

export const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.95 },
  show: { opacity: 1, scale: 1, transition: transitions.spring },
  exit: { opacity: 0, scale: 0.97, transition: transitions.quick },
};

export const slideUpPanel: Variants = {
  hidden: { opacity: 0, y: 24, scale: 0.98 },
  show: { opacity: 1, y: 0, scale: 1, transition: transitions.softSpring },
  exit: { opacity: 0, y: 12, scale: 0.98, transition: transitions.quick },
};

/**
 * Parent for staggered children.
 *
 * `staggerChildren: 0.06` is deliberately small. Much above ~0.08 and a grid
 * of 12 cards takes almost a second to finish arriving, which stops reading
 * as polish and starts reading as slow.
 */
export const staggerParent: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.04 },
  },
};

export const staggerChild: Variants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: transitions.base },
};

// ── Components ─────────────────────────────────────────────────────────

/** Fades and lifts its children in when they scroll into view. Once only. */
export function Reveal({
  children,
  className,
  delay = 0,
  y = 24,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
  y?: number;
}) {
  const reduced = useReducedMotion();

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: reduced ? 0 : y }}
      whileInView={{ opacity: 1, y: 0 }}
      // once: true — re-animating on every scroll past is a novelty that
      // becomes an irritation by the third pass.
      // margin bottom -80px so it fires a beat before the element is fully
      // visible, and the motion is finished by the time it's centred.
      viewport={{ once: true, margin: '0px 0px -80px 0px' }}
      transition={{ ...transitions.slow, delay }}
    >
      {children}
    </motion.div>
  );
}

/** Wraps a grid/list to stagger children in. Children use <StaggerItem>. */
export function Stagger({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <motion.div
      className={className}
      variants={staggerParent}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: '0px 0px -60px 0px' }}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <motion.div variants={staggerChild} className={className}>
      {children}
    </motion.div>
  );
}

/** Page-level enter/exit. Used by the router's outlet. */
export function PageTransition({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={transitions.base}
    >
      {children}
    </motion.div>
  );
}

/** Lifts on hover, presses on click. The default interactive affordance. */
export function Tappable({
  children,
  className,
  lift = 4,
  onClick,
}: {
  children: ReactNode;
  className?: string;
  lift?: number;
  onClick?: () => void;
}) {
  const reduced = useReducedMotion();

  return (
    <motion.div
      className={cn('cursor-pointer', className)}
      whileHover={reduced ? undefined : { y: -lift }}
      whileTap={reduced ? undefined : { scale: 0.98 }}
      transition={transitions.spring}
      onClick={onClick}
    >
      {children}
    </motion.div>
  );
}

/**
 * Number that rolls when it changes — cart totals, counters.
 *
 * Keyed on the value so React remounts and re-runs the enter animation on
 * every change. Without the key it would mutate in place and never animate.
 */
export function AnimatedNumber({
  value,
  className,
}: {
  value: string | number;
  className?: string;
}) {
  const reduced = useReducedMotion();

  if (reduced) return <span className={className}>{value}</span>;

  return (
    <motion.span
      key={String(value)}
      className={cn('inline-block tabular-nums', className)}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={transitions.spring}
    >
      {value}
    </motion.span>
  );
}
